import type { ClientThumb } from './ClientAvatarStack';

export type OrderRef = {
    order_number: string;
    client: ClientThumb | null;
    on_po: boolean;
    on_pi: boolean;
};

function orderDisplayLabel(orderNumber: string): string {
    const trimmed = orderNumber.trim();
    const digitsOnly = trimmed.replace(/\D/g, '');

    return digitsOnly !== '' ? digitsOnly : trimmed;
}

function chipClass(ref: OrderRef): string {
    if (ref.on_po && ref.on_pi) {
        return 'bg-emerald-600 text-white ring-emerald-400';
    }
    if (ref.on_po) {
        return 'bg-emerald-100 text-emerald-900 ring-emerald-400';
    }
    if (ref.on_pi) {
        return 'bg-blue-100 text-blue-900 ring-blue-400';
    }

    return 'bg-gray-100 text-gray-800 ring-gray-300';
}

function tooltipLabel(ref: OrderRef): string {
    const parts = [`Pedido ${ref.order_number}`];
    if (ref.on_po && ref.on_pi) {
        parts.push('PO e PI');
    } else if (ref.on_po) {
        parts.push('só na PO');
    } else if (ref.on_pi) {
        parts.push('só na PI');
    }

    return parts.join(' · ');
}

export default function OrderRefAvatarStack({
    refs,
    maxVisible = 5,
}: {
    refs: OrderRef[];
    maxVisible?: number;
}) {
    const list = refs.filter((r) => r.order_number?.trim());
    if (!list.length) {
        return <span className="text-gray-400">—</span>;
    }

    const visible = list.slice(0, maxVisible);
    const hidden = list.slice(maxVisible);
    const hasMismatch = list.some((r) => r.on_po !== r.on_pi);

    return (
        <div className="flex min-w-0 flex-wrap items-center gap-1">
            {visible.map((ref) => (
                <span
                    key={ref.order_number}
                    title={tooltipLabel(ref)}
                    className={`inline-flex h-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-white px-2 font-mono text-[10px] font-bold leading-none tabular-nums shadow-sm ring-1 ${chipClass(ref)}`}
                >
                    {orderDisplayLabel(ref.order_number)}
                </span>
            ))}
            {hidden.length > 0 ? (
                <span
                    title={hidden.map(tooltipLabel).join('\n')}
                    className="inline-flex h-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-white bg-primary-lighter px-1.5 text-[9px] font-bold text-primary-dark ring-1 ring-gray-200"
                >
                    +{hidden.length}
                </span>
            ) : null}
            {hasMismatch ? (
                <span
                    className="shrink-0 cursor-pointer text-[9px] font-bold text-amber-700"
                    title="Pedidos diferentes entre PO e PI"
                >
                    ≠
                </span>
            ) : null}
        </div>
    );
}
